import { IProduct } from '@/types/product-type';
import { Injectable } from '@angular/core';
import { of, Observable } from 'rxjs';
import product_data from '@/data/product-data';
import { map } from 'rxjs/operators';
import { LotItemUserNote } from '@viewbid/ts-angular-client';
import { ActivatedRoute, Params, Router } from '@angular/router';

const all_products = product_data;

@Injectable({
    providedIn: 'root',
})
export class ProductService {
    public filter_offcanvas = false;
    public showNotes = false;
    public lotItemUserNotes: LotItemUserNote[] = [];
    public notesAuctionId: string = '';
    public notesLotId: string = '';

    // Get Products
    public get products(): Observable<IProduct[]> {
        return of(product_data);
    }

    constructor(
        protected route: ActivatedRoute,
        protected router: Router,
    ) {}

    activeImg: string | undefined;

    handleImageActive(img: string) {
        this.activeImg = img;
    }
    initializeNotesPanelData(
        lotItemUserNotes: LotItemUserNote[],
        auctionId: string,
        lotId: string,
    ) {
        this.lotItemUserNotes = lotItemUserNotes;
        this.notesAuctionId = auctionId;
        this.notesLotId = lotId;
    }

    // Get Products By id
    public getProductById(id: string): Observable<IProduct | undefined> {
        return this.products.pipe(
            map((items) => {
                const product = items.find((p) => p.id === id);
                if (product) {
                    this.handleImageActive(product.img);
                }
                return product;
            }),
        );
    }
    // Get related Products
    public getRelatedProducts(
        productId: string,
        category: string,
    ): Observable<IProduct[]> {
        return this.products.pipe(
            map((items) => {
                return items.filter(
                    (p) =>
                        p.category.toLowerCase() === category.toLowerCase() &&
                        p.id !== productId,
                );
            }),
        );
    }
    // Get max price
    public get maxPrice(): number {
        const max_price = all_products.reduce((max, product) => {
            return product.price > max ? product.price : max;
        }, 0);
        return max_price;
    }
    // shop filterSelect
    public filterSelect = [
        { value: 'asc', text: 'Default Sorting' },
        { value: 'low', text: 'Low to High' },
        { value: 'high', text: 'High to Low' },
        { value: 'on-sale', text: 'On Sale' },
    ];

    // Get Product Filter
    public filterProducts(filter: any = []): Observable<IProduct[]> {
        return this.products.pipe(
            map((product) =>
                product.filter((item: IProduct) => {
                    if (!filter.length) return true;
                    const Tags = filter.some((prev: any) => {
                        if (item.tags) {
                            if (item.tags.includes(prev)) {
                                return prev;
                            }
                        }
                    });
                    return Tags;
                }),
            ),
        );
    }

    // Sorting Filter
    public sortProducts(products: IProduct[], payload: string): any {
        if (payload === 'asc') {
            return products.sort((a, b) => {
                if (a.id < b.id) {
                    return -1;
                } else if (a.id > b.id) {
                    return 1;
                }
                return 0;
            });
        } else if (payload === 'on-sale') {
            return products.filter((p) => p.discount > 0);
        } else if (payload === 'low') {
            return products.sort((a, b) => {
                if (a.price < b.price) {
                    return -1;
                } else if (a.price > b.price) {
                    return 1;
                }
                return 0;
            });
        } else if (payload === 'high') {
            return products.sort((a, b) => {
                if (a.price > b.price) {
                    return -1;
                } else if (a.price < b.price) {
                    return 1;
                }
                return 0;
            });
        }
    }

    /*
    ---------------------------------------------
    ------------- Product Pagination  -----------
    ---------------------------------------------
  */
    public getPager(
        totalItems: number,
        currentPage = 1,
        pageSize = 10,
        addQuery = true,
        sortBy?: string,
    ): any {
        const queryParams: Params = {
            page: currentPage,
            sortBy: sortBy
        };

        // Empty result sets come through as page 0 — never write that to the
        // URL: a pushed "?page=0" entry makes the browser Back button step to
        // the same page minus the param instead of actually leaving.
        if (addQuery && currentPage >= 1) {
            const currentUrlPage: number = Number(this.route.snapshot.queryParams['page']) || 1;
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams,
                queryParamsHandling: 'merge',
                replaceUrl: currentPage === currentUrlPage,
            });
        }

        // calculate total pages
        const totalPages: number = Math.ceil(totalItems / pageSize);

        // Paginate Range
        const paginateRange: number = 3;

        // ensure current page isn't out of range
        if (currentPage < 1) {
            currentPage = 1;
        } else if (currentPage > totalPages) {
            currentPage = totalPages;
        }

        let startPage: number, endPage: number;
        if (totalPages <= paginateRange) {
            startPage = 1;
            endPage = totalPages;
        } else if (currentPage < paginateRange) {
            startPage = 1;
            endPage = paginateRange;
        } else if (currentPage === paginateRange) {
            startPage = 2;
            endPage = paginateRange + 1;
        } else if (currentPage + paginateRange - 1 >= totalPages) {
            startPage = totalPages - paginateRange + 1;
            endPage = totalPages;
        } else {
            startPage = currentPage - 1;
            endPage = currentPage + 1;
        }

        // calculate start and end item indexes
        let startIndex: number = (currentPage - 1) * pageSize;
        const endIndex: number = Math.min(
            startIndex + pageSize - 1,
            totalItems - 1,
        );
        if (endIndex < 0) {
            startIndex = -1;
        }

        // create an array of pages to ng-repeat in the pager control
        const pages: number[] = Array.from(
            Array(endPage - startPage + 1).keys(),
        ).map((i: number) => startPage + i);

        // return object with all pager properties required by the view
        return {
            totalItems: totalItems,
            currentPage: currentPage,
            pageSize: pageSize,
            totalPages: totalPages,
            startPage: startPage,
            endPage: endPage,
            startIndex: startIndex + 1,
            endIndex: endIndex + 1,
            pages: pages,
        };
    }
}
